import { useState, useEffect } from "react";
import client, { apiError } from "../../api/client";
import { toast } from "sonner";
import { X, Loader2, Download } from "lucide-react";

const FIELDS = [
  ["name", "Název školy", true],
  ["ico", "IČO", false],
  ["street", "Ulice a č.p.", false],
  ["city", "Město", false],
  ["zip", "PSČ", false],
  ["district", "Okres", false],
  ["contact_person", "Kontaktní osoba (ředitel)", false],
  ["email", "E-mail", false],
  ["phone", "Telefon", false],
];

const EMPTY = { name: "", ico: "", street: "", city: "", zip: "", district: "", contact_person: "", email: "", phone: "", note: "" };

export default function SchoolEditModal({ school, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);
  const isEdit = !!school?.id;

  useEffect(() => {
    setForm({ ...EMPTY, ...(school || {}) });
  }, [school]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchAres = async () => {
    if (!form.ico.trim()) return;
    setAresLoading(true);
    try {
      const res = await client.get(`/schools/ares/${form.ico.trim()}`);
      setForm((f) => ({
        ...f,
        name: f.name || res.data.name,
        street: res.data.street || f.street,
        city: res.data.city || f.city,
        zip: res.data.zip || f.zip,
        district: res.data.district || f.district,
      }));
      toast.success("Údaje doplněny z registru ARES");
    } catch (err) {
      toast.error(apiError(err, "ARES: subjekt nenalezen"));
    } finally {
      setAresLoading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Vyplňte název školy");
    setSaving(true);
    try {
      const res = isEdit
        ? await client.put(`/schools/${school.id}`, form)
        : await client.post("/schools", form);
      toast.success(isEdit ? "Škola uložena" : "Škola vytvořena");
      onSaved(res.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div
        className="bg-background rounded-lg border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-auto"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="school-edit-modal"
      >
        <div className="flex items-center justify-between px-5 h-14 border-b border-border sticky top-0 bg-background">
          <h3 className="font-heading font-semibold">{isEdit ? "Upravit školu" : "Nová škola"}</h3>
          <button onClick={onClose} data-testid="school-modal-close" className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          {FIELDS.map(([key, label, required]) => (
            <div key={key} className={key === "name" ? "col-span-2" : ""}>
              <label className="block text-xs text-muted-foreground mb-1">
                {label} {required && <span className="text-destructive">*</span>}
              </label>
              <div className="flex gap-2">
                <input
                  data-testid={`school-field-${key}`}
                  value={form[key] || ""}
                  onChange={(e) => set(key, e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20"
                />
                {key === "ico" && (
                  <button
                    type="button"
                    data-testid="ares-lookup-btn"
                    onClick={fetchAres}
                    disabled={aresLoading}
                    className="shrink-0 h-9 px-3 rounded-md border border-border text-sm hover:bg-secondary flex items-center gap-1"
                    title="Doplnit z ARES"
                  >
                    {aresLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    ARES
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="col-span-2">
            <label className="block text-xs text-muted-foreground mb-1">Poznámka</label>
            <textarea
              data-testid="school-field-note"
              value={form.note || ""}
              onChange={(e) => set("note", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-background">
          <button onClick={onClose} className="h-9 px-4 rounded-md border border-border text-sm hover:bg-secondary">
            Zrušit
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="school-save-btn"
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Uložit
          </button>
        </div>
      </div>
    </div>
  );
}
